function featureEnabled(snapshot, key) {
  const items = (((snapshot || {}).entitlements || {}).features || []);
  const match = items.find((item) => item.key === key);
  return Boolean(match && match.enabled);
}

function valueOf(source, key) {
  if (source && typeof source.get === "function") {
    return source.get(key);
  }
  if (source && Object.prototype.hasOwnProperty.call(source, key)) {
    return source[key];
  }
  return null;
}

function checked(source, key) {
  const value = valueOf(source, key);
  return value === true || value === "on";
}

function optionalInteger(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function integerOrFallback(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function formatToggle(enabled) {
  return enabled ? "enabled" : "disabled";
}

function destinationSummary(settings) {
  if (!settings) {
    return "not configured";
  }
  if (settings.notification_destination_name) {
    return `${settings.notification_destination_name} (${settings.notification_destination_status || "unknown"})`;
  }
  if (settings.webhook_url) {
    return "direct webhook";
  }
  return settings.destination_type || "event_log";
}

function maintenanceRows(maintenance) {
  const runs = ((maintenance || {}).runs || []).slice(0, 4);
  if (!runs.length) {
    return [
      {
        label: "Recent runs",
        value: "No maintenance history yet",
        status: "stale",
        meta: "Scheduled maintenance has not written any queue runs for this tenant.",
      },
    ];
  }
  return runs.map((item) => ({
    label: item.run_kind,
    value: item.status,
    status: item.status === "completed" ? "ready" : item.status === "skipped_backpressure" ? "stale" : "overdue",
    meta: item.completed_at || item.started_at,
  }));
}

function statusTone(status) {
  if (["ready", "live_ready", "sandbox_locked"].includes(status)) {
    return "ready";
  }
  if (["needs_configuration", "needs_upgrade", "action_required"].includes(status)) {
    return "stale";
  }
  if (["attention_required", "overdue"].includes(status)) {
    return "overdue";
  }
  return "muted";
}

function messagingGoLiveRows(readiness = {}) {
  const checks = Array.isArray(readiness.checks) ? readiness.checks : [];
  return {
    statusLabel: String(readiness.overall_status || "unknown").replaceAll("_", " "),
    tone: statusTone(readiness.overall_status),
    summary: readiness.summary || "Messaging go-live posture has not been evaluated.",
    rows: [
      {
        label: "Network delivery",
        value: readiness.network_delivery_blocked ? "Sandbox locked" : "Live enabled",
        status: readiness.network_delivery_blocked ? "ready" : "overdue",
        meta: readiness.network_delivery_blocked
          ? "Twilio network sends remain blocked until live mode is explicitly approved."
          : "Live SMS delivery is enabled. Monitor audit, opt-out, and support posture closely.",
      },
      {
        label: "Runtime mode",
        value: readiness.live_sending_enabled ? "live approved" : readiness.send_mode || "sandbox",
        status: readiness.live_sending_enabled ? "overdue" : "ready",
        meta: "Both live mode and live approval must be enabled before network sends can occur.",
      },
      {
        label: "Sender profile",
        value: `${readiness.sender_profile_count || 0} ready`,
        status: (readiness.sender_profile_count || 0) > 0 ? "ready" : "stale",
        meta: "Requires an active approved sender profile with a Messaging Service SID.",
      },
      {
        label: "Twilio account",
        value: `${readiness.active_twilio_account_count || 0} active`,
        status: (readiness.active_twilio_account_count || 0) > 0 ? "ready" : "stale",
        meta: "Tenant must have an active Twilio account connection before live send approval.",
      },
      {
        label: "Webhooks",
        value: `${readiness.status_callback_configured ? "status" : "status missing"} / ${readiness.inbound_webhook_configured ? "inbound" : "inbound missing"}`,
        status: readiness.status_callback_configured && readiness.inbound_webhook_configured ? "ready" : "stale",
        meta: "Status callbacks prove delivery; inbound webhooks capture replies and opt-outs.",
      },
      ...checks.map((item) => ({
        label: String(item.key || "").replaceAll("_", " "),
        value: item.status || "unknown",
        status: statusTone(item.status),
        meta: item.summary || "",
      })),
    ],
  };
}

function normalizeLabel(value) {
  return String(value || "unknown").replaceAll("_", " ");
}

function roleLabel(value) {
  return normalizeLabel(value).replace(/\b\w/g, (match) => match.toUpperCase());
}

function activeRoleCount(workspaceSummary, role) {
  return Number(((workspaceSummary || {}).active_role_counts || {})[role] || 0);
}

function onboardingStepTone(status) {
  if (["completed", "ready"].includes(status)) {
    return "ready";
  }
  if (status === "blocked_by_plan") {
    return "stale";
  }
  if (["action_required", "attention_required", "needs_configuration"].includes(status)) {
    return "overdue";
  }
  return "muted";
}

function stepTarget(step) {
  const key = String((step || {}).key || "");
  if (key === "frontline_coverage" || key === "owner_coverage" || (step || {}).action_path === "/api/workspace/members") {
    return "admin-onboarding-roster";
  }
  if (key === "automation_review") {
    return "admin-settings-automation-section";
  }
  if (key === "alerts_review") {
    return "admin-settings-alerts-section";
  }
  if (key === "digests_review") {
    return "admin-settings-digests-section";
  }
  if (key === "reporting") {
    return "admin-reporting-controls";
  }
  return "admin-settings-tenant-section";
}

function planLockedFeatures(controlCenter) {
  const featureLabels = {
    queue_automation: "Queue automation",
    queue_alerts: "Queue alerts",
    queue_digests: "Queue digests",
    notification_destinations: "Shared notification destinations",
  };
  return Object.entries(featureLabels)
    .filter(([key]) => !featureEnabled(controlCenter, key))
    .map(([key, label]) => ({ key, label }));
}

function readinessIssueRows(checks) {
  return (checks || []).filter((item) => ["attention_required", "needs_configuration", "action_required", "overdue"].includes(item.status));
}

function onboardingStep(onboarding, key) {
  return ((onboarding || {}).steps || []).find((step) => step.key === key) || null;
}

function firstRunStep(status, input) {
  const tone = status === "complete" ? "ready" : status === "active" ? "stale" : "muted";
  return {
    status,
    tone,
    label: input.label,
    detail: input.detail,
    targetId: input.targetId,
    actionLabel: input.actionLabel,
  };
}

export function buildOwnerLaunchReadiness(controlCenter, options = {}) {
  const role = options.role || "operator";
  const locked = role !== "tenant_owner";
  const onboarding = controlCenter.onboarding || {};
  const workspaceSummary = controlCenter.workspace_summary || {};
  const readinessChecks = controlCenter.readiness_checks || [];
  const queueSla = controlCenter.queue_sla || {};
  const queueSummary = controlCenter.queue_summary || {};
  const smoke = controlCenter.smoke_governance || {};
  const messaging = controlCenter.messaging_readiness || {};
  const ownerTotal = activeRoleCount(workspaceSummary, "tenant_owner");
  const frontlineTotal =
    activeRoleCount(workspaceSummary, "operator") +
    activeRoleCount(workspaceSummary, "manager") +
    activeRoleCount(workspaceSummary, "analyst");
  const planLocks = planLockedFeatures(controlCenter);
  const readinessIssues = readinessIssueRows(readinessChecks);
  const actionableStep = (onboarding.steps || []).find((step) =>
    ["action_required", "attention_required", "needs_configuration"].includes(step.status)
  );
  const unsafeLiveMessaging = Boolean(messaging.live_sending_enabled || !messaging.network_delivery_blocked);
  const workspaceReady = ownerTotal > 0 && frontlineTotal > 0;
  const onboardingReady = onboarding.status === "ready";
  const smokeReady = smoke.rotation_status === "ready";
  const messagingReady = !unsafeLiveMessaging && ["sandbox_locked", "ready", "live_ready"].includes(messaging.overall_status);
  const setupReady = onboardingReady && workspaceReady && smokeReady && messagingReady && readinessIssues.length === 0;
  const actionNeeded = !workspaceReady || !onboardingReady || !smokeReady || readinessIssues.length > 0;
  const statusLabel = unsafeLiveMessaging
    ? "Live messaging review required"
    : setupReady
      ? "Ready for sandbox launch"
      : actionNeeded
        ? "Action needed"
        : "Setup in progress";
  const tone = unsafeLiveMessaging ? "overdue" : setupReady ? "ready" : actionNeeded ? "stale" : "muted";
  const nextActions = [];

  if (ownerTotal < 1) {
    nextActions.push({
      label: "Add an active owner",
      detail: "At least one active tenant owner must remain available for launch support and customer administration.",
      tone: "overdue",
      targetId: "admin-onboarding-roster",
      actionLabel: "Review roster",
    });
  }
  if (frontlineTotal < 1) {
    nextActions.push({
      label: "Activate a frontline user",
      detail: "Add or activate at least one manager, analyst, or operator before the queue is customer-ready.",
      tone: "overdue",
      targetId: "admin-onboarding-roster",
      actionLabel: "Review roster",
    });
  }
  if (actionableStep) {
    nextActions.push({
      label: actionableStep.title || "Complete onboarding step",
      detail: actionableStep.summary || onboarding.next_step_summary || "Continue the onboarding checklist.",
      tone: onboardingStepTone(actionableStep.status),
      targetId: stepTarget(actionableStep),
      actionLabel: actionableStep.action_label || "Open setup",
    });
  }
  if (!smokeReady) {
    nextActions.push({
      label: "Confirm smoke rotation",
      detail: smoke.recommended_action || "Record a current smoke credential rotation before launch validation.",
      tone: "stale",
      targetId: "admin-governance",
      actionLabel: "Review smoke",
    });
  }
  if (unsafeLiveMessaging) {
    nextActions.push({
      label: "Return messaging to sandbox safety",
      detail: "Live network sends should stay blocked until explicit go-live approval is complete.",
      tone: "overdue",
      targetId: "admin-readiness",
      actionLabel: "Review messaging",
    });
  }
  if (!nextActions.length) {
    nextActions.push({
      label: "Run the launch smoke pack",
      detail: "Use the go-live helper commands to validate health, assets, queue workflows, and owner visibility before the next release.",
      tone: "ready",
      targetId: "admin-onboarding-helpers",
      actionLabel: "Open helpers",
    });
  }

  return {
    locked,
    statusLabel,
    tone,
    summary: setupReady
      ? "The owner workspace, onboarding checklist, smoke posture, and sandbox messaging guard are ready for a controlled customer trial."
      : "Use this owner launch view to resolve setup gaps without treating Starter plan locks as production failures.",
    launchCards: [
      {
        label: "Onboarding",
        value: onboarding.status || "unknown",
        detail: `${onboarding.completed_steps || 0}/${onboarding.total_steps || 0} complete; ${onboarding.blocked_steps || 0} blocked by plan`,
        tone: onboardingReady ? "ready" : "stale",
      },
      {
        label: "Workspace",
        value: `${ownerTotal} owner / ${frontlineTotal} frontline`,
        detail: `${workspaceSummary.inactive_total || 0} inactive memberships`,
        tone: workspaceReady ? "ready" : "overdue",
      },
      {
        label: "Messaging",
        value: unsafeLiveMessaging ? "live review" : "sandbox locked",
        detail: messaging.summary || "Network delivery remains sandbox/test only.",
        tone: unsafeLiveMessaging ? "overdue" : "ready",
      },
      {
        label: "Plan gates",
        value: planLocks.length ? `${planLocks.length} blocked by plan` : "none blocked",
        detail: planLocks.length ? `${planLocks.map((item) => item.label).join(", ")} stay locked on this plan.` : "Premium gates are available on this plan.",
        tone: planLocks.length ? "stale" : "ready",
      },
    ],
    checklistRows: [
      {
        label: "Owner coverage",
        value: `${ownerTotal} active`,
        status: ownerTotal > 0 ? "ready" : "overdue",
        meta: "Tenant owner coverage for account administration and launch support.",
      },
      {
        label: "Frontline coverage",
        value: `${frontlineTotal} active`,
        status: frontlineTotal > 0 ? "ready" : "overdue",
        meta: "Manager, analyst, or operator coverage for queue work.",
      },
      {
        label: "Queue posture",
        value: `${queueSummary.open_total || 0} open`,
        status: (queueSla.first_response_overdue_total || 0) > 0 ? "stale" : "ready",
        meta: `${queueSla.overdue_total || 0} overdue, ${queueSla.first_response_overdue_total || 0} first-response overdue.`,
      },
      {
        label: "Smoke posture",
        value: smoke.rotation_status || "unknown",
        status: smokeReady ? "ready" : "stale",
        meta: smoke.recommended_action || "Smoke account is current.",
      },
      {
        label: "Readiness checks",
        value: `${readinessChecks.length - readinessIssues.length}/${readinessChecks.length || 0} clear`,
        status: readinessIssues.length ? "stale" : "ready",
        meta: readinessIssues.length ? `${readinessIssues.length} check(s) need owner attention.` : "No active readiness check needs owner attention.",
      },
      {
        label: "Starter plan gates",
        value: planLocks.length ? "blocked by plan" : "available",
        status: planLocks.length ? "stale" : "ready",
        meta: planLocks.length
          ? "These are expected plan limits, not launch-blocking production failures."
          : "No owner-visible plan gates are currently blocking premium workflow setup.",
      },
    ],
    nextActions,
    planLocks,
    readinessIssues: readinessIssues.map((item) => ({
      label: normalizeLabel(item.key),
      value: item.status,
      status: statusTone(item.status),
      meta: item.summary || "Readiness check needs attention.",
    })),
  };
}

export function buildOwnerFirstRunSetupFlow(controlCenter, options = {}) {
  const launch = buildOwnerLaunchReadiness(controlCenter, options);
  const onboarding = controlCenter.onboarding || {};
  const workspaceSummary = controlCenter.workspace_summary || {};
  const smoke = controlCenter.smoke_governance || {};
  const queuePolicyStep = onboardingStep(onboarding, "queue_policy_review");
  const ownerTotal = activeRoleCount(workspaceSummary, "tenant_owner");
  const frontlineTotal =
    activeRoleCount(workspaceSummary, "operator") +
    activeRoleCount(workspaceSummary, "manager") +
    activeRoleCount(workspaceSummary, "analyst");
  const queuePolicyComplete = queuePolicyStep
    ? ["completed", "ready", "blocked_by_plan"].includes(queuePolicyStep.status)
    : !(controlCenter.queue_settings || {}).is_default;
  const workspaceComplete = ownerTotal > 0 && frontlineTotal > 0;
  const smokeComplete = smoke.rotation_status === "ready" && (smoke.last_smoke_status === "success" || smoke.last_successful_smoke_at);
  const launchComplete = launch.tone === "ready";
  const verificationComplete = launchComplete && smokeComplete;
  const doneFlags = [queuePolicyComplete, workspaceComplete, smokeComplete, verificationComplete, launchComplete];
  const firstOpenIndex = doneFlags.findIndex((done) => !done);
  const stepStatus = (index) => (doneFlags[index] ? "complete" : index === firstOpenIndex ? "active" : "pending");
  const steps = [
    firstRunStep(stepStatus(0), {
      label: "Confirm owner settings",
      detail: queuePolicyStep?.summary || "Review tenant profile, queue pull limits, and manager control defaults before inviting customers.",
      targetId: "admin-settings-tenant-section",
      actionLabel: "Open settings",
    }),
    firstRunStep(stepStatus(1), {
      label: "Seat the working team",
      detail: `${ownerTotal} owner and ${frontlineTotal} frontline user(s) are active for first-run queue work.`,
      targetId: "admin-onboarding-roster",
      actionLabel: "Review roster",
    }),
    firstRunStep(stepStatus(2), {
      label: "Validate sandbox smoke",
      detail: smokeComplete
        ? `Smoke account ${smoke.canonical_email || "credential"} is current.`
        : smoke.recommended_action || "Confirm the smoke account, rotation state, and most recent sandbox login.",
      targetId: "admin-governance",
      actionLabel: "Open smoke",
    }),
    firstRunStep(stepStatus(3), {
      label: "Run launch verification",
      detail: "Run health, asset, and queue smoke checks after the owner settings and roster are ready.",
      targetId: "admin-onboarding-helpers",
      actionLabel: "Open helpers",
    }),
    firstRunStep(stepStatus(4), {
      label: "Return to ready state",
      detail: launch.statusLabel === "Ready for sandbox launch" ? launch.summary : "Resolve the active launch item, then return here to verify ready state.",
      targetId: "admin-readiness",
      actionLabel: "Review readiness",
    }),
  ];
  const completedTotal = steps.filter((step) => step.status === "complete").length;
  const currentStep = steps.find((step) => step.status === "active") || steps.find((step) => step.status === "pending") || steps[steps.length - 1];

  return {
    statusLabel: completedTotal === steps.length ? "First run ready" : `${completedTotal}/${steps.length} complete`,
    tone: completedTotal === steps.length ? "ready" : currentStep.status === "active" ? "stale" : "muted",
    summary: completedTotal === steps.length
      ? "Owner setup, team coverage, sandbox smoke, and launch verification are ready for a customer-facing trial."
      : "Follow this first-run path to move from owner setup into validation without leaving the Admin workspace.",
    progressPercent: Math.round((completedTotal / steps.length) * 100),
    currentStep,
    steps,
  };
}

export function buildOwnerCommandCenter(controlCenter, options = {}) {
  const launch = buildOwnerLaunchReadiness(controlCenter, options);
  const queueSla = controlCenter.queue_sla || {};
  const queueSummary = controlCenter.queue_summary || {};
  const accessRequests = controlCenter.access_requests || {};
  const messaging = controlCenter.messaging_readiness || {};
  const onboarding = controlCenter.onboarding || {};
  const pendingAccessRequests = Number(accessRequests.pending_total || 0);
  const overdueTotal = Number(queueSla.overdue_total || 0);
  const firstResponseOverdue = Number(queueSla.first_response_overdue_total || 0);
  const primaryAction = launch.nextActions[0] || {
    label: "Review launch readiness",
    detail: launch.summary,
    tone: launch.tone,
    targetId: "admin-readiness",
    actionLabel: "Open launch plan",
  };
  const actionCards = launch.nextActions.slice(0, 3);
  const secondaryActions = [
    {
      label: "Review launch plan",
      detail: "Open the readiness checklist, plan gates, and messaging safety review.",
      tone: launch.tone,
      targetId: "admin-readiness",
      actionLabel: "Open readiness",
    },
    {
      label: "Open onboarding",
      detail: onboarding.next_step_summary || "Review setup steps, recommended defaults, and smoke helpers.",
      tone: onboarding.status === "ready" ? "ready" : "stale",
      targetId: "admin-onboarding-journey",
      actionLabel: "Open onboarding",
    },
    {
      label: "Review rollout pack",
      detail: "Check role walkthroughs, browser validation, and deployment routine before release.",
      tone: launch.tone === "ready" ? "ready" : "muted",
      targetId: "admin-release-pack",
      actionLabel: "Open rollout pack",
    },
  ];
  if (pendingAccessRequests > 0) {
    secondaryActions.unshift({
      label: "Review access requests",
      detail: `${pendingAccessRequests} public request(s) are waiting for owner review.`,
      tone: "overdue",
      targetId: "admin-access-requests",
      actionLabel: "Open requests",
    });
  }

  return {
    headline: launch.statusLabel,
    tone: launch.tone,
    detail: launch.summary,
    primaryAction,
    actionCards,
    secondaryActions: secondaryActions.slice(0, 4),
    commandCards: [
      {
        label: "Launch stage",
        value: launch.statusLabel,
        detail: `${onboarding.completed_steps || 0}/${onboarding.total_steps || 0} onboarding steps complete`,
        tone: launch.tone,
      },
      {
        label: "Owner action",
        value: primaryAction.label,
        detail: primaryAction.detail,
        tone: primaryAction.tone,
      },
      {
        label: "Queue pressure",
        value: `${overdueTotal} overdue`,
        detail: `${queueSummary.open_total || 0} open conversations, ${firstResponseOverdue} first-response overdue`,
        tone: overdueTotal > 0 || firstResponseOverdue > 0 ? "stale" : "ready",
      },
      {
        label: "Customer mode",
        value: messaging.network_delivery_blocked ? "sandbox only" : "live review",
        detail: messaging.network_delivery_blocked
          ? "Network SMS sends remain blocked while the app is prepared for customers."
          : "Live network sends are enabled and require owner/support monitoring.",
        tone: messaging.network_delivery_blocked ? "ready" : "overdue",
      },
    ],
  };
}

export function buildOwnerTeamSetup(controlCenter, options = {}) {
  const role = options.role || "operator";
  const actorMembershipId = options.membershipId || null;
  const locked = role !== "tenant_owner";
  const members = Array.isArray(controlCenter.workspace_members) ? controlCenter.workspace_members : [];
  const workspaceSummary = controlCenter.workspace_summary || {};
  const ownerTotal = activeRoleCount(workspaceSummary, "tenant_owner");
  const managerTotal = activeRoleCount(workspaceSummary, "manager");
  const operatorTotal = activeRoleCount(workspaceSummary, "operator");
  const analystTotal = activeRoleCount(workspaceSummary, "analyst");
  const frontlineTotal = managerTotal + operatorTotal + analystTotal;
  const inactiveTotal = Number(workspaceSummary.inactive_total || 0);
  const pendingInviteTotal = members.filter((item) => item.status === "invited").length;
  const ready = ownerTotal > 0 && frontlineTotal > 0;
  const recommendedRole = frontlineTotal < 1 ? "operator" : managerTotal < 1 ? "manager" : "operator";
  const roleOptions = [
    { value: "operator", label: "Operator" },
    { value: "manager", label: "Manager" },
    { value: "analyst", label: "Analyst" },
    { value: "viewer", label: "Viewer" },
    { value: "tenant_owner", label: "Tenant Owner" },
  ];

  return {
    locked,
    title: "Owner Team Setup",
    statusLabel: locked ? "Review only" : ready ? "Team ready" : "Invite frontline",
    tone: locked ? "stale" : ready ? "ready" : "overdue",
    summary: locked
      ? "Managers can inspect workspace coverage, but tenant owners manage seats and invite links."
      : ready
        ? "Owner and frontline coverage are in place. Invite links can still be created here as the team grows."
        : "Invite at least one active operator, analyst, or manager before customer-facing launch.",
    cards: [
      {
        label: "Owners",
        value: String(ownerTotal),
        detail: "Tenant owner coverage for administration and launch support.",
        tone: ownerTotal > 0 ? "ready" : "overdue",
      },
      {
        label: "Frontline",
        value: String(frontlineTotal),
        detail: `${managerTotal} manager, ${operatorTotal} operator, ${analystTotal} analyst.`,
        tone: frontlineTotal > 0 ? "ready" : "overdue",
      },
      {
        label: "Invites",
        value: String(pendingInviteTotal),
        detail: `${pendingInviteTotal} pending invite(s), ${inactiveTotal} inactive membership(s).`,
        tone: pendingInviteTotal > 0 ? "stale" : "muted",
      },
    ],
    invite: {
      disabled: locked,
      defaultRole: recommendedRole,
      defaultExpiresInHours: 168,
      helperText: locked
        ? "Only tenant owners can create invite links."
        : "Create a secure invite link, send it to the teammate, and have them set their own password.",
      roleOptions,
    },
    rosterRows: members
      .slice()
      .sort((left, right) => {
        const statusRank = { active: 0, invited: 1, inactive: 2 };
        return (
          (statusRank[left.status] ?? 9) - (statusRank[right.status] ?? 9)
          || String(left.role || "").localeCompare(String(right.role || ""))
          || String(left.full_name || left.email || "").localeCompare(String(right.full_name || right.email || ""))
        );
      })
      .map((member) => ({
        membershipId: member.membership_id,
        label: member.full_name || member.email || "Unknown member",
        value: roleLabel(member.role),
        role: member.role || "viewer",
        roleOptions,
        email: member.email || "email unavailable",
        status: member.status || "unknown",
        statusLabel:
          member.status === "active"
            ? "Active"
            : member.status === "invited"
              ? "Invite pending"
              : member.status === "inactive"
                ? "Inactive"
                : roleLabel(member.status || "unknown"),
        tone: member.status === "active" ? "ready" : member.status === "invited" ? "stale" : "muted",
        isSelf: Boolean(actorMembershipId && member.membership_id === actorMembershipId),
        isLastActiveOwner: member.status === "active" && member.role === "tenant_owner" && ownerTotal <= 1,
      }))
      .map((member) => ({
        ...member,
        canUpdateRole: !locked && member.status === "active" && !member.isLastActiveOwner,
        canDeactivate: !locked && member.status === "active" && !member.isSelf && !member.isLastActiveOwner,
        canReactivate: !locked && member.status === "inactive",
        actionDetail: locked
          ? "Review only."
          : member.isLastActiveOwner
            ? "Protected: this is the last active tenant owner."
            : member.isSelf
              ? "You cannot deactivate your own membership."
              : member.status === "invited"
                ? "Waiting for invite acceptance."
                : member.status === "inactive"
                  ? "Reactivate when this teammate should regain access."
                  : "Owner-managed seat.",
      })),
  };
}

export function buildAdminControlSummary(controlCenter, options = {}) {
  const role = options.role || "operator";
  const locked = role !== "tenant_owner";
  const automationEnabled = featureEnabled(controlCenter, "queue_automation");
  const alertsEnabled = featureEnabled(controlCenter, "queue_alerts");
  const digestsEnabled = featureEnabled(controlCenter, "queue_digests");
  const notificationEnabled = featureEnabled(controlCenter, "notification_destinations");
  const readinessChecks = controlCenter.readiness_checks || [];
  const readyCount = readinessChecks.filter((item) => item.status === "ready").length;
  const attentionCount = readinessChecks.filter((item) => item.status === "attention_required").length;
  const needsConfigCount = readinessChecks.filter((item) => item.status === "needs_configuration").length;
  const queueSla = controlCenter.queue_sla || {};
  const queueSettings = controlCenter.queue_settings || {};
  const automation = controlCenter.automation_settings || {};
  const alerts = controlCenter.alert_settings || {};
  const digests = controlCenter.digest_settings || {};
  const smoke = controlCenter.smoke_governance || {};
  const workspaceSummary = controlCenter.workspace_summary || {};
  const accessRequests = controlCenter.access_requests || {};

  return {
    locked,
    automationLocked: locked || !automationEnabled,
    alertsLocked: locked || !alertsEnabled,
    digestsLocked: locked || !digestsEnabled,
    notificationLocked: !notificationEnabled,
    metrics: [
      {
        label: "Active workspace",
        value: workspaceSummary.active_total || 0,
        detail: `${workspaceSummary.inactive_total || 0} inactive memberships`,
      },
      {
        label: "Open queue",
        value: (controlCenter.queue_summary || {}).open_total || 0,
        detail: `${(queueSla.awaiting_response_total || 0)} awaiting first response`,
      },
      {
        label: "Overdue",
        value: queueSla.overdue_total || 0,
        detail: `${queueSla.first_response_overdue_total || 0} first-response overdue`,
      },
      {
        label: "Readiness",
        value: `${readyCount}/${readinessChecks.length || 1}`,
        detail: `${attentionCount} attention, ${needsConfigCount} needs configuration`,
      },
      {
        label: "Access requests",
        value: accessRequests.pending_total || 0,
        detail: `${accessRequests.approved_total || 0} approved, ${accessRequests.deferred_total || 0} deferred`,
      },
    ],
    profileRows: [
      {
        label: "Tenant",
        value: controlCenter.tenant_profile.name,
        status: "ready",
        meta: controlCenter.tenant_profile.slug,
      },
      {
        label: "Plan",
        value: controlCenter.entitlements.plan_label,
        status: controlCenter.entitlements.legacy_defaulted ? "stale" : "ready",
        meta: controlCenter.tenant_profile.subscription_plan || "legacy default",
      },
      {
        label: "Timezone",
        value: controlCenter.tenant_profile.timezone,
        status: "muted",
        meta: `Updated ${controlCenter.tenant_profile.updated_at || "n/a"}`,
      },
      {
        label: "Workspace coverage",
        value: `${workspaceSummary.active_role_counts?.tenant_owner || 0} owner(s)`,
        status: (workspaceSummary.active_role_counts?.tenant_owner || 0) > 0 ? "ready" : "overdue",
        meta: `${workspaceSummary.active_role_counts?.manager || 0} manager(s), ${workspaceSummary.active_role_counts?.operator || 0} operator(s)`,
      },
    ],
    smokeRows: [
      {
        label: "Canonical smoke",
        value: smoke.canonical_email || "not set",
        status: smoke.canonical_email ? "ready" : "stale",
        meta: smoke.canonical_role || "unknown role",
      },
      {
        label: "Rotation status",
        value: smoke.rotation_status || "unknown",
        status: smoke.rotation_status === "ready" ? "ready" : smoke.rotation_status === "due" ? "stale" : "overdue",
        meta: `Every ${smoke.rotation_interval_days || 0} days`,
      },
      {
        label: "Last smoke",
        value: smoke.last_successful_smoke_at || smoke.last_smoke_event_at || "not recorded",
        status: smoke.last_smoke_status === "success" ? "ready" : smoke.last_smoke_status ? "stale" : "muted",
        meta: smoke.last_smoke_email || smoke.canonical_email || "no smoke history",
      },
      {
        label: "Recommended action",
        value: smoke.recommended_action || "No action required",
        status: smoke.rotation_status === "ready" ? "ready" : "stale",
        meta: smoke.rotation_notes || "No notes saved",
      },
    ],
    queueRows: [
      {
        label: "Pull policy",
        value: `${queueSettings.default_pull_limit}/${queueSettings.max_pull_limit}`,
        status: queueSettings.is_default ? "stale" : "ready",
        meta: "default/max pull limit",
      },
      {
        label: "Operator pull",
        value: formatToggle(queueSettings.operator_pull_enabled),
        status: queueSettings.operator_pull_enabled ? "ready" : "stale",
        meta: "self-serve queue pickup",
      },
      {
        label: "Manager actions",
        value: [
          queueSettings.manager_dispatch_enabled ? "dispatch" : null,
          queueSettings.manager_reassign_enabled ? "reassign" : null,
          queueSettings.manager_release_enabled ? "release" : null,
        ]
          .filter(Boolean)
          .join(", ") || "none",
        status:
          queueSettings.manager_dispatch_enabled || queueSettings.manager_reassign_enabled || queueSettings.manager_release_enabled
            ? "ready"
            : "stale",
        meta: "supervisory queue controls",
      },
      {
        label: "Inactive auto-close",
        value: queueSettings.auto_close_after_inactive_hours ? `${queueSettings.auto_close_after_inactive_hours} hours` : "disabled",
        status: queueSettings.auto_close_after_inactive_hours ? "ready" : "muted",
        meta: "conversation inactivity threshold",
      },
    ],
    automationRows: [
      {
        label: "Auto assign",
        value: automationEnabled ? formatToggle(automation.auto_assign_enabled) : "plan locked",
        status: !automationEnabled ? "stale" : automation.auto_assign_enabled ? "ready" : "muted",
        meta: `${automation.auto_assign_batch_size || 0} per run`,
      },
      {
        label: "Open work cap",
        value: automation.auto_assign_max_open_per_user ? `${automation.auto_assign_max_open_per_user} open/user` : "uncapped",
        status: automationEnabled ? "muted" : "stale",
        meta: "assignment balancing limit",
      },
      {
        label: "Overdue escalation",
        value: automationEnabled ? formatToggle(automation.auto_escalate_overdue_enabled) : "plan locked",
        status: !automationEnabled ? "stale" : automation.auto_escalate_overdue_enabled ? "ready" : "muted",
        meta: `${automation.auto_escalate_overdue_batch_size || 0} per run`,
      },
    ],
    deliveryRows: [
      {
        label: "Queue alerts",
        value: alertsEnabled ? formatToggle(alerts.enabled) : "plan locked",
        status: !alertsEnabled ? "stale" : alerts.enabled ? "ready" : "muted",
        meta: `${destinationSummary(alerts)} | cooldown ${alerts.cooldown_minutes || 0}m`,
      },
      {
        label: "Alert thresholds",
        value: [
          alerts.overdue_total_threshold ? `overdue ${alerts.overdue_total_threshold}` : null,
          alerts.overdue_unassigned_threshold ? `unassigned ${alerts.overdue_unassigned_threshold}` : null,
          alerts.first_response_overdue_threshold ? `response ${alerts.first_response_overdue_threshold}` : null,
          alerts.automation_skipped_overdue_threshold ? `skipped ${alerts.automation_skipped_overdue_threshold}` : null,
        ]
          .filter(Boolean)
          .join(" | ") || "thresholds not set",
        status: alertsEnabled ? "muted" : "stale",
        meta: notificationEnabled ? "Notification bindings available" : "Notifications locked on current plan",
      },
      {
        label: "Digests",
        value: digestsEnabled ? formatToggle(digests.enabled) : "plan locked",
        status: !digestsEnabled ? "stale" : digests.enabled ? "ready" : "muted",
        meta: `${destinationSummary(digests)} | ${digests.delivery_hour_utc ?? 0}:00 UTC`,
      },
      {
        label: "Digest cadence",
        value: [digests.daily_enabled ? "daily" : null, digests.weekly_enabled ? "weekly" : null].filter(Boolean).join(", ") || "none",
        status: digestsEnabled ? "muted" : "stale",
        meta: notificationEnabled ? "Shared destinations can be reused here" : "Shared destinations locked on current plan",
      },
    ],
    messagingGoLive: messagingGoLiveRows(controlCenter.messaging_readiness || {}),
    maintenanceRows: maintenanceRows(controlCenter.maintenance),
  };
}

export function buildAdminUpdatePayload(controlCenter, source) {
  const queueSettings = controlCenter.queue_settings || {};
  const automation = controlCenter.automation_settings || {};
  const alerts = controlCenter.alert_settings || {};
  const digests = controlCenter.digest_settings || {};
  const body = {
    tenant_profile: {
      name: valueOf(source, "tenant_name"),
      subscription_plan: valueOf(source, "subscription_plan") || null,
      timezone: valueOf(source, "timezone"),
    },
    queue_settings: {
      default_pull_limit: integerOrFallback(valueOf(source, "default_pull_limit"), queueSettings.default_pull_limit),
      max_pull_limit: integerOrFallback(valueOf(source, "max_pull_limit"), queueSettings.max_pull_limit),
      operator_pull_enabled: checked(source, "operator_pull_enabled"),
      manager_dispatch_enabled: checked(source, "manager_dispatch_enabled"),
      manager_reassign_enabled: checked(source, "manager_reassign_enabled"),
      manager_release_enabled: checked(source, "manager_release_enabled"),
      auto_close_after_inactive_hours: optionalInteger(valueOf(source, "auto_close_after_inactive_hours")),
    },
  };
  if (featureEnabled(controlCenter, "queue_automation")) {
    body.automation_settings = {
      auto_assign_enabled: checked(source, "auto_assign_enabled"),
      auto_assign_batch_size: integerOrFallback(valueOf(source, "auto_assign_batch_size"), automation.auto_assign_batch_size),
      auto_assign_max_open_per_user: optionalInteger(valueOf(source, "auto_assign_max_open_per_user")),
      auto_escalate_overdue_enabled: checked(source, "auto_escalate_overdue_enabled"),
      auto_escalate_overdue_batch_size: integerOrFallback(
        valueOf(source, "auto_escalate_overdue_batch_size"),
        automation.auto_escalate_overdue_batch_size
      ),
    };
  }
  if (featureEnabled(controlCenter, "queue_alerts")) {
    body.alert_settings = {
      enabled: checked(source, "alerts_enabled"),
      destination_type: alerts.destination_type,
      webhook_url: alerts.webhook_url,
      notification_destination_id: alerts.notification_destination_id,
      cooldown_minutes: integerOrFallback(valueOf(source, "cooldown_minutes"), alerts.cooldown_minutes),
      overdue_total_threshold: optionalInteger(valueOf(source, "overdue_total_threshold")),
      overdue_unassigned_threshold: optionalInteger(valueOf(source, "overdue_unassigned_threshold")),
      first_response_overdue_threshold: optionalInteger(valueOf(source, "first_response_overdue_threshold")),
      automation_skipped_overdue_threshold: optionalInteger(valueOf(source, "automation_skipped_overdue_threshold")),
    };
  }
  if (featureEnabled(controlCenter, "queue_digests")) {
    body.digest_settings = {
      enabled: checked(source, "digest_enabled"),
      destination_type: digests.destination_type,
      webhook_url: digests.webhook_url,
      notification_destination_id: digests.notification_destination_id,
      daily_enabled: checked(source, "daily_enabled"),
      weekly_enabled: checked(source, "weekly_enabled"),
      delivery_hour_utc: integerOrFallback(valueOf(source, "delivery_hour_utc"), digests.delivery_hour_utc),
    };
  }
  return body;
}
